// The First Mile PICKLIST section — the pick-queue twin of the GRN section, with the same
// Blocks|Table toggle and the same per-warehouse treatment.
//
//   Blocks — a headline pair (Picklist Picked / Picklist Escalations) then one card per warehouse,
//            each carrying its own SLA-bucket tiles.
//   Table  — one row per warehouse plus a Total row; columns Picked | Escalations | Breakdown.
//
// There is no "exclusions" concept here: a pick either met the 30-minute floor-time SLA or it
// didn't. Both buckets are timed from when the picklist was RAISED (PICK_REQUESTED).
import { Card, Col, Row, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import styles from "../index.module.scss";
import {
  FirstMilePickCell,
  FirstMilePicklistEscalationsResponse,
  PicklistEscalationDrilldownKey,
} from "../control_tower.api";
import {
  FIRST_MILE_DOCK_LABEL,
  FIRST_MILE_WAREHOUSES,
  PICKLIST_BUCKET_ORDER,
  PICKLIST_BUCKET_SHORT,
  PICKLIST_DRILLDOWN_LABEL,
  pickRates,
} from "../first_mile_meta";
import { FirstMileWarehouseKey } from "../types";
import SectionViewToggle, { ViewMode } from "./section_view_toggle";

type Drill = (bucket: PicklistEscalationDrilldownKey, dock?: FirstMileWarehouseKey) => void;

interface Props {
  loading: boolean;
  data: FirstMilePicklistEscalationsResponse | null;
  onDrill: Drill;
  // The main "Picklist Picked" card drills into the pick list itself, not an SLA bucket.
  onPickedClick: (dock?: FirstMileWarehouseKey) => void;
}

interface RowData {
  key: string;
  dock?: FirstMileWarehouseKey; // undefined on the Total row
  label: string;
  cell?: FirstMilePickCell;
}

const CompletionBody: React.FC<{ cell?: FirstMilePickCell }> = ({ cell }) => {
  const r = pickRates(cell);
  return (
    <>
      <div className={styles.grnPercent}>{r.pct}%</div>
      <div className={styles.grnSub}>
        {r.picked} of {r.total} picked
      </div>
    </>
  );
};

const tilesFor = (cell?: FirstMilePickCell) =>
  PICKLIST_BUCKET_ORDER.map((k) => ({
    key: k as PicklistEscalationDrilldownKey,
    label: PICKLIST_BUCKET_SHORT[k],
    value: cell?.buckets?.[k] ?? 0,
  }));

const FirstMilePicklistSection: React.FC<Props> = ({ loading, data, onDrill, onPickedClick }) => {
  const [view, setView] = useState<ViewMode>("blocks");
  const totals = data?.totals;
  const byWh = data?.by_warehouse;
  const sla = data?.sla_minutes ?? 30;

  const rows: RowData[] = [
    ...FIRST_MILE_WAREHOUSES.map((w) => ({
      key: w,
      dock: w,
      label: FIRST_MILE_DOCK_LABEL[w],
      cell: byWh?.[w],
    })),
    { key: "__total", label: "Total", cell: totals },
  ];

  const columns: ColumnsType<RowData> = [
    { title: "Warehouse", dataIndex: "label", key: "label", render: (v: string) => <strong>{v}</strong> },
    {
      title: "Picklist Picked",
      key: "picked",
      render: (_, r) => {
        const p = pickRates(r.cell);
        return (
          <div>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#389e0d" }}>{p.pct}%</span>{" "}
            <span className={styles.cellMuted}>
              {p.picked} of {p.total}
            </span>
          </div>
        );
      },
    },
    {
      title: "Escalations",
      key: "escalations",
      render: (_, r) => (
        <Tooltip title={`Picks breaching the ${sla}-minute SLA`}>
          <span className={styles.cellLink} onClick={() => onDrill("all", r.dock)}>
            {r.cell?.escalations ?? 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Breakdown",
      key: "breakdown",
      render: (_, r) => (
        <div className={styles.cellMuted} style={{ display: "flex", gap: 12 }}>
          {PICKLIST_BUCKET_ORDER.map((k) => (
            <span key={k} className={styles.cellLink} style={{ fontWeight: 400, color: "#8c8c8c" }}
                  onClick={() => onDrill(k, r.dock)}>
              {PICKLIST_BUCKET_SHORT[k]} {r.cell?.buckets?.[k] ?? 0}
            </span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionHeadTitle}>Picklist</span>
        <SectionViewToggle value={view} onChange={setView} />
      </div>

      {view === "table" ? (
        <Table
          rowKey="key"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} lg={8}>
              <Tooltip title="Pick legs picked out of all due in this dispatch window. Click to list them.">
                <Card
                  size="small"
                  loading={loading}
                  className={styles.overallCard}
                  style={{ borderLeft: "3px solid #52c41a", height: "100%" }}
                  onClick={() => onPickedClick()}
                >
                  <div className={styles.blockTitle}>Picklist Picked</div>
                  <CompletionBody cell={totals} />
                </Card>
              </Tooltip>
            </Col>

            <Col xs={24} lg={16}>
              <Card
                size="small"
                loading={loading}
                style={{ border: "1px solid #e6e8ec", borderLeft: "3px solid #f5222d", borderRadius: 10, height: "100%" }}
              >
                <div className={styles.escHeader}>
                  <span className={styles.blockTitle} style={{ marginBottom: 0 }}>
                    Picklist Escalations
                  </span>
                  <Tooltip title={`All picks breaching the ${sla}-minute SLA in this window. Click for the full list.`}>
                    <span className={styles.escTotal} onClick={() => onDrill("all")}>
                      {totals?.escalations ?? 0}
                    </span>
                  </Tooltip>
                </div>
                <div className={styles.escGrid}>
                  {tilesFor(totals).map((t) => (
                    <Tooltip key={t.key} title={PICKLIST_DRILLDOWN_LABEL[t.key]}>
                      <div className={styles.escBucket} onClick={() => onDrill(t.key)}>
                        <div className={styles.escBucketValue}>{t.value}</div>
                        <div className={styles.escBucketLabel}>{t.label}</div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          <div className={styles.blockTitle} style={{ marginBottom: 10 }}>
            By Warehouse
          </div>
          <Row gutter={16}>
            {FIRST_MILE_WAREHOUSES.map((w) => (
              <Col key={w} xs={24} lg={8}>
                <Card size="small" loading={loading} className={styles.whCard}>
                  <div className={styles.whCardName}>{FIRST_MILE_DOCK_LABEL[w]}</div>
                  <CompletionBody cell={byWh?.[w]} />
                  <div className={styles.whTileRow}>
                    {tilesFor(byWh?.[w]).map((t) => (
                      <Tooltip key={t.key} title={`${FIRST_MILE_DOCK_LABEL[w]} — ${PICKLIST_DRILLDOWN_LABEL[t.key]}`}>
                        <div className={styles.whTile} onClick={() => onDrill(t.key, w)}>
                          <div className={styles.whTileValue}>{t.value}</div>
                          <div className={styles.whTileLabel}>{t.label}</div>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default FirstMilePicklistSection;
